import { AfterViewInit, Directive, HostBinding, InjectionToken, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { ComponentContainer, Stack } from 'golden-layout';
import { BehaviorSubject, Observable } from 'rxjs';
import { WidgetConfig } from '../_models/WidgetConfig';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BrowserHelpers } from '../_helpers/BrowserHelpers';
import { v4 } from 'uuid';
import { debounce } from '../_helpers/tools';
import { Store } from '@ngrx/store';
import { MainStoreState } from '../_store/mainStoreState';
import { getCurrentUser } from '../_store/AccountStore/account.store';
import { hasSessionEdition } from '../_store/SessionStore/session.store';
import { SessionEdition } from '../_models/SessionEdition';

const GoldenLayoutContainerTokenName = 'GoldenLayoutContainer';
export const GoldenLayoutContainerInjectionToken = new InjectionToken<ComponentContainer>(GoldenLayoutContainerTokenName);

@Directive({
  selector: '[appWidgetHost]',
})
export abstract class WidgetHostDirective implements OnInit, AfterViewInit {
  @HostBinding('style.position') hostPosition = 'relative';

  #loading = true;
  public get loading(): boolean {
    return this.#loading;
  }

  public set loading(isLoading: boolean) {
    this.#loading = isLoading;
    this.updateLoadingContainer();
  }

  public bodyHeight$!: BehaviorSubject<number>;
  public bodyHeightAsString$!: BehaviorSubject<string>;
  public existsSessionForCurrentUser$!: Observable<SessionEdition | undefined>;

  protected id: string;
  protected config: WidgetConfig<any>;

  private readonly uid: string;

  private popoutContainer!: HTMLDivElement;
  private popinContainer!: HTMLDivElement;
  private stack!: Stack;
  private stackContainer: HTMLElement | undefined;
  private readonly dockClass = 'lm_dock';
  private readonly arxPopoutClass = 'arx-popout';
  private readonly maximiseClass = 'lm_maximise';
  private readonly closeClass = 'lm_close';

  protected constructor(
    protected container: ComponentContainer,
    public rootHtmlElement: HTMLElement,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    protected store: Store<MainStoreState>
  ) {
    this.uid = 'loading-container-' + BrowserHelpers.uuidv4();

    // @ts-ignore
    if (
      !container.state ||
      typeof container.state !== 'object' ||
      // @ts-ignore
      !container.state.config
    ) {
      throw new Error('container.state should be an object of type WidgetConfig.');
    }
    // @ts-ignore
    if (!container.state.config.id) {
      throw new Error('container.state.config should contain a variable id (GUID).');
    }

    this.container = container;
    // @ts-ignore
    this.id = this.container.state.config.id;
    // @ts-ignore
    this.config = this.container.state.config;

    this.container.on('resize', () => {
      this.calculateHeight(this.container.height);
    });

    this.calculateHeight(this.container.height);

    this.store.select(getCurrentUser).subscribe((user) => {
      this.existsSessionForCurrentUser$ = this.store.select(hasSessionEdition(user));
    });
  }

  ngOnInit(): void {
    this.createLoadingContainer();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.handleHeaderConfiguration();
    }, 0);
  }

  private updateLoadingContainer(): void {
    const loadingContainer = this.rootHtmlElement.querySelector(`.${this.uid}`);
    if (loadingContainer) {
      this.renderer.setStyle(loadingContainer, 'display', this.loading ? 'flex' : 'none');
    }
  }

  protected calculateHeight(height: number | null): void {
    let bodyHeightStr = height ? height + 'px' : 'auto';
    let bodyHeight = height ? height : 400;

    if (this.config.hasSubHeader && this.rootHtmlElement.children.length > 0) {
      const parentHeight = this.rootHtmlElement.parentElement?.clientHeight || 0;
      bodyHeight = height || parentHeight;
      bodyHeightStr = bodyHeight + 'px';
    }

    if (!this.bodyHeightAsString$) {
      this.bodyHeightAsString$ = new BehaviorSubject<string>(bodyHeightStr);
      this.bodyHeight$ = new BehaviorSubject<number>(bodyHeight);
    } else {
      this.bodyHeightAsString$.next(bodyHeightStr);
      this.bodyHeight$.next(bodyHeight);
    }
  }

  protected destroyPopout(): void {
    if (this.popoutContainer) {
      this.popoutContainer.innerHTML = '';
      this.popoutContainer.remove();
    }
  }

  protected destroyPopin(): void {
    if (this.popinContainer) {
      this.popinContainer.innerHTML = '';
      this.popinContainer.remove();
    }
  }

  protected close(): void {
    this.container.close();
  }

  private createLoadingContainer(): void {
    const loadingContainer = this.renderer.createElement('div');
    this.renderer.addClass(loadingContainer, this.uid);
    this.renderer.setStyle(loadingContainer, 'display', this.loading ? 'flex' : 'none');
    this.renderer.setStyle(loadingContainer, 'position', 'absolute');
    this.renderer.setStyle(loadingContainer, 'zIndex', '100');
    this.renderer.setStyle(loadingContainer, 'backgroundColor', 'rgba(111,111,111,0.4)');
    this.renderer.setStyle(loadingContainer, 'justifyContent', 'center');
    this.renderer.setStyle(loadingContainer, 'alignItems', 'center');
    this.renderer.setStyle(loadingContainer, 'height', '100%');
    this.renderer.setStyle(loadingContainer, 'width', '100%');

    const component = this.viewContainerRef.createComponent(MatProgressSpinner);
    component.instance.diameter = 80;
    component.instance.mode = 'indeterminate';

    this.renderer.appendChild(loadingContainer, component.instance._elementRef.nativeElement);
    this.renderer.appendChild(this.rootHtmlElement, loadingContainer);
  }

  public handleHeaderConfiguration(): void {
    if (this.container.parent.parent) {
      this.stack = this.container.parent.parent as Stack;
      this.stackContainer = this.container.parent.parent.element;
    }

    const dockElement = this.stackContainer?.querySelector(`div.${this.dockClass}`) as HTMLDivElement | null;
    const maximiseElement = this.stackContainer?.querySelector(`div.${this.maximiseClass}`) as HTMLDivElement | null;
    const closeElement = this.stackContainer?.querySelector(`div.${this.closeClass}`) as HTMLDivElement | null;

    const arxPopoutElement = this.stackContainer?.querySelector(`div.${this.arxPopoutClass}`) as HTMLDivElement | null;

    if (dockElement) {
      dockElement.style.display = this.config && this.config.canPin ? 'block' : 'none';
    }

    if (maximiseElement) {
      maximiseElement.style.display = this.config.canMaximize ? 'block' : 'none';
    }
    if (closeElement) {
      if (this.config.canCloseWindow) {
        closeElement.style.display = 'block';
        if (this.config.isPopoutWindow) {
          closeElement.addEventListener('click', (evt) => {
            evt.preventDefault();
            window.close();
          });
        }
      } else {
        closeElement.style.display = 'none';
      }
    }

    if (this.config.canOpenInNewWindow && closeElement && !arxPopoutElement) {
      this.createPopoutElement(closeElement.parentElement);
    }
    if (this.config.canPin && closeElement) {
      this.createPopinElement(closeElement.parentElement);
    }
  }

  private createPopoutElement(container: HTMLElement | null): void {
    if (!container) {
      return;
    }
    this.destroyPopout();
    this.popoutContainer = document.createElement('div');
    this.popoutContainer.title = 'Ouvrir dans une nouvelle fenêtre';
    this.popoutContainer.className = 'material-icons arx-popout';
    this.popoutContainer.textContent = 'open_in_new';
    this.popoutContainer.addEventListener(
      'click',
      debounce(() => {
        this.handlePopout();
      }, 300)
    );
    container.prepend(this.popoutContainer);
  }

  private createPopinElement(container: HTMLElement | null): void {
    if (!container) {
      return;
    }

    this.destroyPopin();
    this.popinContainer = document.createElement('div');
    this.popinContainer.title = 'Renvoyer dans la fenêtre principale';
    this.popinContainer.className = 'material-icons arx-popin';
    this.popinContainer.textContent = 'open_in_new_off';
    this.popinContainer.addEventListener(
      'click',
      debounce(() => {
        this.handlePopin();
      }, 300)
    );
    container.prepend(this.popinContainer);
  }

  private handlePopout(): void {
    console.debug('popout asked');

    /**
     * The options as used in the window.open string
     */
    const features = this.serializeWindowFeatures({
      menubar: 'no',
      toolbar: 'no',
      location: 'no',
      personalbar: 'no',
      resizable: 'yes',
      scrollbars: 'no',
      status: 'no',
      dependent: 'no',
      width: window.outerWidth,
      height: window.outerHeight,
    });

    const windowId = v4();

    const activeComponent = this.stack.getActiveComponentItem();

    const windowRef = window.open(window.location.href, windowId, features);
    if (windowRef) {
      windowRef.addEventListener('load', () => {
        setTimeout(() => {
          if (!activeComponent) {
            console.warn('Aucun composant actif');
            return;
          }

          const config = Object.assign({}, activeComponent.toConfig());
          // @ts-ignore
          const configState = Object.assign({}, config.componentState.config);
          configState.canOpenInNewWindow = false;
          configState.canMaximize = false;
          configState.canCloseWindow = true;
          configState.isPopoutWindow = true;
          configState.canPin = true;

          const message = {
            type: 'popout_widget',
            config: configState,
            widgetType: config.componentType,
          };

          windowRef.postMessage(message, window.location.href);

          activeComponent.close();
          windowRef.focus();

          if (this.stack.contentItems.length === 0) {
            this.destroyPopout();
          }
        }, 100);
      });
    }
  }

  private handlePopin(): void {
    console.debug('has parent', this.config.parentWindow);
    const activeComponent = this.stack.getActiveComponentItem();

    if (!activeComponent) {
      console.warn('Aucun composant actif');
      return;
    }

    if (this.config.parentWindow) {
      this.config.parentWindow.postMessage(
        {
          type: 'popin_widget',
          widgetType: activeComponent.componentType,
        },
        window.location.href
      );

      this.config.parentWindow.focus();
      window.close();
    }
  }

  /**
   * Serialises a map-vec of key:values to a window options string
   *
   * @param windowOptions -
   *
   * @returns serialised window options
   * @internal
   */
  private serializeWindowFeatures(windowOptions: Record<string, string | number>): string {
    const windowOptionsString: string[] = [];

    for (const key in windowOptions) {
      if (windowOptions.hasOwnProperty(key)) {
        windowOptionsString.push(key + '=' + windowOptions[key].toString());
      }
    }

    return windowOptionsString.join(',');
  }
}
